import React, { useEffect, useState } from "react"
import { KButton, KInput, KTitleSpan, KTextArea, KSpan } from "kahuna-base-react-components"
import { Announcement } from "../../../../types"
import { toast } from "react-toastify"
import { errorFilledToastOptions } from "../../../../constants"

type AnnouncementModalProps = {
  open: boolean
  announcement: Announcement | null
  onClose: () => void
  onSave: (announcement: Partial<Announcement>) => void
  loading: boolean
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ open, announcement, onClose, onSave, loading }) => {
  const [title, setTitle] = useState("")
  const [descriptionEn, setDescriptionEn] = useState("")
  const [descriptionTr, setDescriptionTr] = useState("")
  const [descriptionDe, setDescriptionDe] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  useEffect(() => {
    if (open) {
      if (announcement) {
        setTitle(announcement.title || "")
        setDescriptionEn(announcement.descriptionEn || "")
        setDescriptionTr(announcement.descriptionTr || "")
        setDescriptionDe(announcement.descriptionDe || "")
        setStartDate(announcement.startDate ? announcement.startDate.split("T")[0] : "")
        setEndDate(announcement.endDate ? announcement.endDate.split("T")[0] : "")
      } else {
        setTitle("")
        setDescriptionEn("")
        setDescriptionTr("")
        setDescriptionDe("")
        setStartDate("")
        setEndDate("")
      }
    }
  }, [announcement, open])

  const handleSave = () => {
    if (!title.trim()) {
      toast.error("Title is required", errorFilledToastOptions)
      return
    }
    if (!descriptionEn.trim()) {
      toast.error("English description is required", errorFilledToastOptions)
      return
    }
    if (!descriptionTr.trim()) {
      toast.error("Turkish description is required", errorFilledToastOptions)
      return
    }
    if (!descriptionDe.trim()) {
      toast.error("German description is required", errorFilledToastOptions)
      return
    }

    onSave({
      id: announcement?.id,
      title: title.trim(),
      descriptionEn: descriptionEn.trim(),
      descriptionTr: descriptionTr.trim(),
      descriptionDe: descriptionDe.trim(),
      startDate: startDate || null,
      endDate: endDate || null
    })
  }

  if (!open) return null

  const isFormValid = title.trim() && descriptionEn.trim() && descriptionTr.trim() && descriptionDe.trim()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl w-full max-w-[650px] max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
          <KTitleSpan
            text={announcement ? "Edit Announcement" : "Add New Announcement"}
            fontSize={18}
            lineHeight="26px"
            fontWeight={600}
          />
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M1 13L13 1" stroke="#666" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="flex flex-col gap-5">
            {/* Title */}
            <div>
              <div className="flex items-center gap-1 mb-2">
                <KSpan text="Title" color="#374151" fontSize={14} fontWeight={500} />
                <span className="text-red-500">*</span>
              </div>
              <KInput
                value={title}
                onChange={(text: string) => setTitle(text)}
                placeholder="Enter announcement title"
                padding="12px 14px"
                background="#F9FAFB"
                borderRadius={8}
              />
            </div>

            {/* Description EN */}
            <div>
              <div className="flex items-center gap-1 mb-2">
                <KSpan text="Description (English)" color="#374151" fontSize={14} fontWeight={500} />
                <span className="text-red-500">*</span>
              </div>
              <KTextArea
                value={descriptionEn}
                onChange={(text: string) => setDescriptionEn(text)}
                placeholder="Enter English description (supports HTML tags like <b></b>)"
                padding="12px 14px"
                height={90}
                background="#F9FAFB"
                borderRadius={8}
              />
            </div>

            {/* Description TR */}
            <div>
              <div className="flex items-center gap-1 mb-2">
                <KSpan text="Description (Turkish)" color="#374151" fontSize={14} fontWeight={500} />
                <span className="text-red-500">*</span>
              </div>
              <KTextArea
                value={descriptionTr}
                onChange={(text: string) => setDescriptionTr(text)}
                placeholder="Turkce aciklama giriniz"
                padding="12px 14px"
                height={90}
                background="#F9FAFB"
                borderRadius={8}
              />
            </div>

            {/* Description DE */}
            <div>
              <div className="flex items-center gap-1 mb-2">
                <KSpan text="Description (German)" color="#374151" fontSize={14} fontWeight={500} />
                <span className="text-red-500">*</span>
              </div>
              <KTextArea
                value={descriptionDe}
                onChange={(text: string) => setDescriptionDe(text)}
                placeholder="Deutsche Beschreibung eingeben"
                padding="12px 14px"
                height={90}
                background="#F9FAFB"
                borderRadius={8}
              />
            </div>

            {/* Start Date */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <KSpan text="Start Date" color="#374151" fontSize={14} fontWeight={500} />
                <span className="text-xs text-gray-400">(Optional - leave empty to show immediately)</span>
              </div>
              <KInput
                type="date"
                value={startDate}
                onChange={(text: string) => setStartDate(text)}
                padding="12px 14px"
                background="#F9FAFB"
                borderRadius={8}
              />
            </div>

            {/* End Date */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <KSpan text="End Date" color="#374151" fontSize={14} fontWeight={500} />
                <span className="text-xs text-gray-400">(Optional - leave empty for no expiration)</span>
              </div>
              <KInput
                type="date"
                value={endDate}
                onChange={(text: string) => setEndDate(text)}
                padding="12px 14px"
                background="#F9FAFB"
                borderRadius={8}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <KButton
            text="Cancel"
            height="40px"
            padding="0 20px"
            background="#FFF"
            hoverBackground="#F5F5F5"
            onClick={onClose}
            disabled={loading}
          />
          <KButton
            text={loading ? "Saving..." : announcement ? "Update" : "Create"}
            height="40px"
            padding="0 20px"
            background={isFormValid ? "#111" : "#D1D5DB"}
            hoverBackground={isFormValid ? "#333" : "#D1D5DB"}
            textColor="#FFF"
            onClick={handleSave}
            disabled={loading || !isFormValid}
          />
        </div>
      </div>
    </div>
  )
}

export default AnnouncementModal
